import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import {
  ACADEMY_TOPICS,
  AcademyLesson,
  AcademyTopic,
  certificateOfTopic,
} from '../../../academy/academy-catalog';
import { AcademyService, LessonStatus } from '../../../academy/academy.service';

interface Row {
  lesson: AcademyLesson;
  title: string;
  summary: string;
  status: LessonStatus;
  isNew: boolean;
}

/**
 * Ein Themenbereich der Academy (`/academy/topics/:topicId`): Beschreibung,
 * Fortschritt, die Lektionen in Reihenfolge und zu welchem Zertifikat das
 * Thema zählt.
 */
@Component({
  selector: 'app-academy-topic',
  templateUrl: './academy-topic.component.html',
  styleUrls: ['./academy-topic.component.scss'],
})
export class AcademyTopicComponent implements OnInit, OnDestroy {
  topic: AcademyTopic | null = null;
  title = '';
  description = '';
  rows: Row[] = [];
  completed = 0;
  certTitle = '';
  certId = '';
  firstOpen: AcademyLesson | null = null;
  /** Stelle im Lernpfad: „03“ von „08“ — die Themen bauen aufeinander auf. */
  number = '';
  totalTopics = '';
  nextTopic: { topic: AcademyTopic; title: string; number: string } | null = null;
  ready = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private academy: AcademyService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.academy.ensureContent();
    this.academy.loadProgress();
    combineLatest([
      this.route.paramMap,
      this.academy.content$.pipe(filter((c) => !!c)),
      this.academy.progress$,
      this.academy.progressLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params]) => {
        const topic = ACADEMY_TOPICS.find((t) => t.id === params.get('topicId'));
        if (!topic) {
          this.router.navigate(['/academy']);
          return;
        }
        this.build(topic);
        this.ready = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackRow(_: number, r: Row): string {
    return r.lesson.id;
  }

  private build(topic: AcademyTopic): void {
    this.topic = topic;
    const text = this.academy.topicText(topic.id);
    this.title = text.title;
    this.description = text.description;
    this.rows = this.academy.lessonsOfTopic(topic.id).map((lesson) => ({
      lesson,
      title: this.academy.lessonTitle(lesson.id),
      summary: this.academy.lessonText(lesson.id)?.summary ?? '',
      status: this.academy.lessonStatus(lesson.id),
      isNew: this.academy.isNew(lesson),
    }));
    this.completed = this.rows.filter((r) => r.status === 'completed').length;
    this.firstOpen = this.rows.find((r) => r.status !== 'completed')?.lesson ?? null;
    // Nummer und „nächstes Thema“ zählen nur die sichtbaren Themen (ohne Creator fehlt eins).
    const visible = this.academy.topics();
    const i = visible.indexOf(topic);
    this.number = String(i + 1).padStart(2, '0');
    this.totalTopics = String(visible.length).padStart(2, '0');
    const next = visible[i + 1];
    this.nextTopic = next
      ? { topic: next, title: this.academy.topicText(next.id).title, number: String(i + 2).padStart(2, '0') }
      : null;
    const cert = certificateOfTopic(topic.id);
    this.certId = cert?.id ?? '';
    this.certTitle = cert ? this.academy.certificateText(cert.id).title : '';
  }
}
